/**
 * Platform-specific linked URL configuration for Settings.
 *
 * Structure: settingId → platform → url
 *
 * When a SettingCard has a non-empty `learnMoreUrl`, it calls `getLinkedUrl(settingId, fallbackUrl)`
 * which queries the current platform from the bridge and returns the matching URL,
 * or the fallback if no platform-specific override exists.
 */

import { NativePlatform, NativePlatformType } from '../Contracts/base';
import { SettingsNativeBridgeFactory } from '../bridge/SettingsNativeBridgeFactory';

// Map of settingId → per-platform URL overrides
export type LinkedUrlMap = Partial<Record<NativePlatformType, string>>;

export const LINKED_URLS: Record<string, LinkedUrlMap> = {
	'update-channel': {
		[NativePlatform.WINDOWS]:
			'https://docs.citrix.com/en-us/citrix-workspace-app-for-windows/updates',
		[NativePlatform.MAC]:
			'https://docs.citrix.com/en-us/citrix-workspace-app-for-mac/update.html',
	},
	'privacy-send-usage-statistics': {
		[NativePlatform.WINDOWS]:
			'https://docs.citrix.com/en-us/citrix-workspace-app-for-windows/data-collection-and-monitoring#customer-experience-improvement-program-ceip',
		[NativePlatform.MAC]:
			'https://docs.citrix.com/en-us/citrix-workspace-app-for-mac/app-experience/data-collection-and-monitoring.html',
	},
	'support-help-documentation': {
		[NativePlatform.WINDOWS]:
			'https://help-docs.citrix.com/en-us/citrix-workspace-app/windows/',
	},
};

// ============================================================================
// URL resolver
// ============================================================================

/**
 * Resolve the linked URL for a setting on the current platform.
 * Platform is read from the native bridge via SettingsNativeBridgeFactory.
 *
 * @param settingId   - The setting ID (e.g. 'update-channel')
 * @param fallbackUrl - URL to return when no platform-specific override exists
 * @returns The resolved URL, or the fallback
 */
export function getLinkedUrl(
	settingId: string,
	fallbackUrl?: string
): string | undefined {
	const platform = SettingsNativeBridgeFactory.getBridge().getPlatform();
	const platformUrl = LINKED_URLS[settingId]?.[platform];
	return platformUrl ?? fallbackUrl;
}
